import { useMemo } from "react";
import { useOrganization } from "@clerk/react-router";

export interface UseAcomAdminResult {
    /** Whether the current user is an ACOM admin */
    isAcomAdmin: boolean;
    /** Whether Clerk organization data has loaded */
    isLoaded: boolean;
}

/**
 * Hook to determine if the current user is an ACOM admin.
 *
 * Checks two conditions:
 * 1. The Clerk organization name is "ACOM" (case-insensitive)
 * 2. The user's org membership role is "org:admin"
 *
 * This is a UI-only check for showing/hiding admin features.
 * The backend is the authoritative auth check.
 */
export function useAcomAdmin(): UseAcomAdminResult {
    const { organization, membership, isLoaded } = useOrganization();

    const isAcomAdmin = useMemo(() => {
        if (!isLoaded || !organization || !membership) return false;

        const isAcomOrg = organization.name?.toUpperCase() === "ACOM";
        const isAdmin = membership.role === "org:admin";

        return isAcomOrg && isAdmin;
    }, [isLoaded, organization, membership]);

    return { isAcomAdmin, isLoaded };
}
